import { useCallback, useId, useState } from 'react';
import classNames from 'classnames';
import { Navigation, A11y, Mousewheel, FreeMode } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Icon, type IconSize } from '@components/icon';
import { NavItem, type NavItemProps } from '@components/nav/NavItem';
import { useSwiperNavigation } from '@components/utils';

import 'swiper/css';
import 'swiper/css/a11y';

import './SecondaryNav.scss';
export interface SecondaryNavProps {
  /** accessible label for the navigation landmark so screen readers can distinguish this nav from others on the page */
  ariaLabel?: string;
  /** placement of the prev/next arrows. default 'split' */
  arrowPlacement?: 'split' | 'right';
  /** size of the prev/next arrow icons. default 'medium' */
  arrowSize?: IconSize;
  /** toggle scrolled class. default false */
  dark?: boolean;
  /** show or hide top border. default false */
  hasDivider?: boolean;
  /** hides arrows if true. default false */
  isMobile?: boolean;
  /** array of NavItem prop data objects */
  items: Array<NavItemProps>;
  /** alt text for optional brand logo */
  logoAlt?: string;
  /** source for optional brand logo */
  logoSrc?: string;
  /** accessible label for the next-arrow button. default 'Next' */
  nextLabel?: string;
  /** accessible label for the previous-arrow button. default 'Previous' */
  prevLabel?: string;
  /** trailing CTA slot — sits on the right and wraps to full-width on mobile breakpoint */
  trailingAction?: React.ReactNode;
  /** additional class names */
  className?: string;
  /** additional class names applied to the swiper wrapper element */
  wrapperClassName?: string;
  /** additional class names applied to the prev arrow button */
  prevClassName?: string;
  /** additional class names applied to the next arrow button */
  nextClassName?: string;
  /** additional class names applied to the trailing action wrapper */
  trailingActionClassName?: string;
  /** fires on init, slide change, and resize with current edge state */
  onSwiperEdgeChange?: (edgeState: { isBeginning: boolean; isEnd: boolean }) => void;
  /** opt-in override for nav item text casing. defaults to the brand's `--nav-item-*-text-case` token */
  textCase?: 'uppercase' | 'lowercase' | 'capitalize' | 'none';
  /** when true, slides intercept clicks in capture phase before NavItem's inner <a> fires stopPropagation */
  captureItemClicks?: boolean;
  /** additional Swiper configuration merged with internal config; `onAfterInit`, `onResize`, `onSlideChange`, and `onSwiper` are composed with internal handlers, not replaced */
  swiperProps?: Partial<React.ComponentProps<typeof Swiper>>;
}

/**
 * SecondaryNav Component
 * @param ariaLabel - accessible label for the navigation landmark (`role="navigation"`); used to distinguish this nav from others on the page
 * @param arrowPlacement - placement of the prev/next arrows. default 'split'
 * @param arrowSize - size of the prev/next arrow icons. default 'medium'
 * @param dark - toggle scrolled class
 * @param hasDivider - show or hide top border
 * @param isMobile - turns off arrows
 * @param items - Array of NavItem labels
 * @param logoAlt - alt text for optional brand logo
 * @param logoSrc - source for optional brand logo
 * @param nextLabel - accessible label for the next-arrow button
 * @param prevLabel - accessible label for the previous-arrow button
 * @param trailingAction - trailing CTA slot
 * @param className - additional class names
 * @param wrapperClassName - additional class names applied to the swiper wrapper element
 * @param prevClassName - additional class names applied to the prev arrow button
 * @param nextClassName - additional class names applied to the next arrow button
 * @param trailingActionClassName - additional class names applied to the trailing action wrapper
 * @param onSwiperEdgeChange - fires on init, slide change, and resize with current edge state
 * @param textCase - opt-in override for nav item text casing
 * @param captureItemClicks - when true, slides intercept clicks in capture phase before NavItem's inner anchor fires stopPropagation
 * @param swiperProps - additional Swiper configuration merged with internal config; `onAfterInit`, `onResize`, `onSlideChange`, and `onSwiper` are composed with internal handlers, not replaced
 */
export const SecondaryNav: React.FC<SecondaryNavProps> = ({
  ariaLabel,
  arrowPlacement = 'split',
  arrowSize = 'medium',
  dark,
  hasDivider,
  isMobile,
  items,
  logoSrc,
  logoAlt,
  nextLabel = 'Next',
  prevLabel = 'Previous',
  trailingAction,
  className,
  wrapperClassName,
  prevClassName,
  nextClassName,
  trailingActionClassName,
  onSwiperEdgeChange,
  textCase,
  captureItemClicks = false,
  swiperProps,
  ...rest
}) => {
  const { isSwiperDisabled, handleOnResize, prevRef, nextRef, swiperRef } = useSwiperNavigation({
    initialDisabled: !isMobile,
    shouldSkipUpdate: () => Boolean(isMobile),
    updateDependencies: [items],
  });

  // ensure unique class names for swiper navigation to prevent conflicts
  // with multiple instances on the same page
  const navId = useId().replace(/:/g, '');
  const prevSelectorClass = `nav-swiper-button-prev-${navId}`;
  const nextSelectorClass = `nav-swiper-button-next-${navId}`;

  // track edge state separately so we can sync aria-disabled per arrow
  const [edgeState, setEdgeState] = useState({ isBeginning: true, isEnd: false });

  const onSwiperEvent = useCallback(
    (swiper: SwiperType) => {
      setEdgeState(prev =>
        prev.isBeginning === swiper.isBeginning && prev.isEnd === swiper.isEnd
          ? prev
          : { isBeginning: swiper.isBeginning, isEnd: swiper.isEnd },
      );
      handleOnResize(swiper);
      onSwiperEdgeChange?.({ isBeginning: swiper.isBeginning, isEnd: swiper.isEnd });
    },
    [handleOnResize, onSwiperEdgeChange],
  );

  const {
    onAfterInit: externalOnAfterInit,
    onResize: externalOnResize,
    onSlideChange: externalOnSlideChange,
    onSwiper: externalOnSwiper,
    mousewheel: externalMousewheel,
    freeMode: externalFreeMode,
    modules: externalModules,
    className: swiperClassName,
    ...restSwiperProps
  } = swiperProps ?? {};

  // Deep-merge mousewheel: always start with enabled:false to avoid SSR hydration mismatch,
  // then re-enable in onAfterInit only when the consumer intends it to be active.
  const mousewheelConfig = (() => {
    const base = { forceToAxis: true, releaseOnEdges: true };
    if (externalMousewheel == null) return { ...base, enabled: false };
    if (externalMousewheel === false) return false;
    // boolean true or object: normalise to object so SSR defer always applies
    const external = typeof externalMousewheel === 'object' ? externalMousewheel : {};
    // enabled:false must be last so the SSR defer cannot be bypassed by a consumer spread
    return { ...base, ...external, enabled: false };
  })();

  // Re-enable for null (default), boolean true, or an object where the consumer hasn't
  // explicitly disabled mousewheel.
  const shouldEnableMousewheel =
    typeof mousewheelConfig === 'object' &&
    (externalMousewheel == null ||
      externalMousewheel === true ||
      (typeof externalMousewheel === 'object' && externalMousewheel.enabled !== false));

  // Deep-merge freeMode so callers can override individual options without losing the rest.
  const freeModeConfig = (() => {
    const base = { enabled: true, sticky: true, momentum: true };
    if (externalFreeMode == null) return base;
    if (typeof externalFreeMode !== 'object') return externalFreeMode;
    return { ...base, ...externalFreeMode };
  })();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.code === 'Enter' || e.code === 'Space') {
      e.stopPropagation();
    }
  };

  const initialSlide = Math.max(
    0,
    items.findIndex(item => item.active),
  );

  const slides = items.map(
    ({ label, active, ariaLabel, onClick: itemOnClick, ...itemProps }, index) => {
      const keySource = (itemProps as Record<string, unknown>).id ?? itemProps.href ?? label;
      const slideKey =
        typeof keySource === 'string' || typeof keySource === 'number'
          ? String(keySource)
          : `slide_${index}`;
      return (
        <SwiperSlide
          key={slideKey}
          onClickCapture={
            captureItemClicks && itemOnClick
              ? (e: React.MouseEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  itemOnClick(e as unknown as Parameters<NonNullable<typeof itemOnClick>>[0]);
                }
              : undefined
          }>
          <NavItem
            {...itemProps}
            label={label}
            active={active}
            onClick={itemOnClick}
            ariaLabel={
              ariaLabel ?? (label ? `${label}, ${index + 1} of ${items.length}` : undefined)
            }
            className="vds-secondary-nav__item"
            variant="secondary"
          />
        </SwiperSlide>
      );
    },
  );

  const prevButton = (
    <button
      type="button"
      className={classNames(prevSelectorClass, 'vds-secondary-nav__prev', prevClassName)}
      aria-label={prevLabel}
      aria-disabled={edgeState.isBeginning}
      ref={element => {
        if (prevRef) {
          prevRef.current = element;
        }
      }}
      onKeyDown={handleKeyDown}>
      <Icon size={arrowSize} name="chevron-backward" />
    </button>
  );

  const nextButton = (
    <button
      type="button"
      className={classNames(nextSelectorClass, 'vds-secondary-nav__next', nextClassName)}
      aria-label={nextLabel}
      aria-disabled={edgeState.isEnd}
      ref={element => {
        if (nextRef) {
          nextRef.current = element;
        }
      }}
      onKeyDown={handleKeyDown}>
      <Icon size={arrowSize} name="chevron-forward" />
    </button>
  );

  return (
    <div
      role="navigation"
      aria-label={ariaLabel}
      className={classNames(
        'vds-secondary-nav',
        {
          'vds-secondary-nav--arrows-right': arrowPlacement === 'right',
          'vds-secondary-nav--dark': dark,
          'vds-secondary-nav--disabled': isSwiperDisabled || isMobile,
          'vds-secondary-nav--divider': hasDivider,
          'vds-secondary-nav--has-trailing-action': Boolean(trailingAction),
          [`vds-secondary-nav--text-case-${textCase}`]: Boolean(textCase),
        },
        className,
      )}
      {...rest}>
      {arrowPlacement === 'split' && prevButton}
      <div className={classNames('vds-secondary-nav__wrapper', wrapperClassName)}>
        <Swiper
          slidesPerView="auto"
          {...restSwiperProps}
          className={classNames('vds-secondary-nav__swiper', swiperClassName)}
          modules={[
            A11y,
            Navigation,
            Mousewheel,
            FreeMode,
            ...(Array.isArray(externalModules) ? externalModules : []),
          ]}
          navigation={{ prevEl: `.${prevSelectorClass}`, nextEl: `.${nextSelectorClass}` }}
          initialSlide={initialSlide}
          freeMode={freeModeConfig}
          mousewheel={mousewheelConfig}
          // suppresses default "x / y" labels and redundant group role
          a11y={{ slideLabelMessage: '', slideRole: 'none' }}
          onSwiper={instance => {
            swiperRef.current = instance;
            externalOnSwiper?.(instance);
          }}
          onResize={swiper => {
            onSwiperEvent(swiper);
            externalOnResize?.(swiper);
          }}
          onAfterInit={swiper => {
            onSwiperEvent(swiper);
            if (shouldEnableMousewheel) swiper.mousewheel.enable();
            externalOnAfterInit?.(swiper);
          }}
          onSlideChange={swiper => {
            onSwiperEvent(swiper);
            externalOnSlideChange?.(swiper);
          }}>
          {slides}
        </Swiper>
      </div>
      {arrowPlacement === 'right' && prevButton}
      {nextButton}
      {logoSrc && (
        <NavItem className="vds-secondary-nav__logo" logoSrc={logoSrc} logoAlt={logoAlt} />
      )}
      {trailingAction && (
        <div className={classNames('vds-secondary-nav__trailing-action', trailingActionClassName)}>
          {trailingAction}
        </div>
      )}
    </div>
  );
};
